import { memo, useEffect, useRef, useMemo } from 'react';
import { Group, Line, Transformer } from 'react-konva';
import { useSelector, useDispatch } from 'react-redux';
import Konva from 'konva';
import { RootState } from 'store';
import { setInvalidOperation, updateMovedPanelsPosition, setToggleAddSelectedMovablePanel } from 'store/slices/PanelSlice';
import { toggleHideSatelliteOverlay, setActiveDragFunctionality, setMapOnlyFrozenPoint } from 'store/slices/ToolSlice';
import { useRefs } from 'contexts/RefContext';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { generatePolygon, doesPolygonOverLap, isCompletelyInsideFacet, normalizePointsToStage, findTargetFacetByCoverage, bakeGroupTransform, getSnappedPositionForMultiplePanels, ensureClosedPolygon, rotatePoint } from '../../PanelUtil';
import { KonvaGroupNames, resizePoints } from '../../utils';
import InvalidFacetEditToast from '../InvalidModal';
import { appToast } from 'components/Basic/CustomToast';
import { KonvaEventObject } from 'konva/lib/Node';

type PanelInfo = {
	orientation: string;
	originSegmentId: string;
	exteriorCoords: number[][];
	panelId: string;
	roofSegmentId: string;
	isValid: boolean;
};

function SelectedMovablePanelsGroup(): JSX.Element {
	const dispatch = useDispatch();
	const { selectedMovablePanels, isInvalidPanelOperation, panelHistory } = useSelector((state: RootState) => state.panelSlice.data);
	const { hideSatelliteOverlay, activeDragFunctionality, mapOnlyFrozenPoint } = useSelector((state: RootState) => state.toolNewPostions.data);
	const scale = useKonvaImageScale();
	const { konvaRef } = useRefs();

	const groupRef = useRef<Konva.Group>(null);
	const transformerRef = useRef<Konva.Transformer>(null);
	const isDraggingRef = useRef(false);
	const wasHiddenRef = useRef(hideSatelliteOverlay);

	const originalCenterByGroup = useRef<Map<string, { cx: number; cy: number }>>(new Map());
	const allMovablePanelsGroupRef = useRef<Konva.Group[]>([]);

	const allFacetsRef = useRef<Konva.Line[]>([]);
	const allStablePanels = useRef<Konva.Line[]>([]);
	const intersectedFacetRef = useRef<Konva.Line | null>(null);

	const { allRoofSegs } = useSelector((state: RootState) => state.roofData.data);

	const panelsByFacet = useMemo(() => {
		const groups: {
			[groupId: string]: {
				panels: any[];
				currentAzimuth: number;
				originalPitch: number;
				centerX: number;
				centerY: number;
			};
		} = {};

		selectedMovablePanels.forEach(eachPanel => {
			if (!groups[eachPanel.segmentId]) {
				const facetId = eachPanel.segmentId;
				const facetDetails = allRoofSegs.find(eachSeg => eachSeg.id === facetId);
				if (facetDetails) {
					groups[eachPanel.segmentId] = {
						currentAzimuth: facetDetails.azimuthDegrees,
						originalPitch: facetDetails.pitchDegrees,
						panels: [],
						centerX: 0,
						centerY: 0
					};
				}
			}
			groups[eachPanel.segmentId].panels.push(eachPanel);
		});

		// Normalize points for each group
		Object.keys(groups).forEach(facetId => {
			const groupData = groups[facetId];

			// Calculate bounding box center
			let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
			groupData.panels.forEach(p => {
				const pts = resizePoints(p.exteriorCoords.flat(), scale);
				for (let i = 0; i < pts.length; i += 2) {
					minX = Math.min(minX, pts[i]);
					maxX = Math.max(maxX, pts[i]);
					minY = Math.min(minY, pts[i + 1]);
					maxY = Math.max(maxY, pts[i + 1]);
				}
			});

			const cx = (minX + maxX) / 2;
			const cy = (minY + maxY) / 2;
			groupData.centerX = cx;
			groupData.centerY = cy;

			// Normalize points (un-rotate to make flat rectangles)
			groupData.panels = groupData.panels.map(p => {
				const rawPoints = resizePoints(p.exteriorCoords.flat(), scale);
				const normalizedPoints: number[] = [];

				for (let i = 0; i < rawPoints.length; i += 2) {
					const relX = rawPoints[i] - cx;
					const relY = rawPoints[i + 1] - cy;
					// Rotate backwards by azimuth to flatten
					const unRotated = rotatePoint(relX, relY, -groupData.currentAzimuth);
					normalizedPoints.push(unRotated.x, unRotated.y);
				}

				return {
					...p,
					normalizedPoints: normalizedPoints
				};
			});
		});

		return groups;
	}, [allRoofSegs, selectedMovablePanels, scale]);

	// ------------- Transformer Setup -------------
	useEffect(() => {
		const transformer = transformerRef.current;
		const group = groupRef.current;
		const stage = konvaRef?.current;
		if (!transformer || !group || !stage) return;

		group.position({ x: 0, y: 0 });

		requestAnimationFrame(() => {
			if (selectedMovablePanels.length > 0) {
				transformer.nodes([group]);
				transformer.enabledAnchors([]);
				transformer.rotateEnabled(false);
				transformer.borderStroke('rgba(0,123,255,0.8)');
				transformer.borderStrokeWidth(2);
				transformer.padding(5);
				transformer.forceUpdate();
				transformer.moveToTop();
			} else {
				transformer.nodes([]);
			}
			stage.batchDraw();
		});
	}, [selectedMovablePanels, konvaRef]);

	useEffect(() => {
		const TOAST_ID = 'invalid-panel-move-error';
		if (isInvalidPanelOperation) {
			appToast.sticky(
				<InvalidFacetEditToast
					shouldDisable={!panelHistory.length}
					showExit={hideSatelliteOverlay}
					onExit={() => dispatch(toggleHideSatelliteOverlay())}
				/>,
				{
					toastId: TOAST_ID,
					className: 'invalid-error-toast',
					containerId: 'tool-toast'
				}
			);
		} else if (appToast.isActive(TOAST_ID)) {
			appToast.dismiss({ id: TOAST_ID, containerId: 'tool-toast' });
		}
	}, [isInvalidPanelOperation, panelHistory.length, hideSatelliteOverlay]);

	const DRAG_FUNCTIONALITY = 'movePanel';

	// When map only turns on mid-drag, freeze the selected-panels group: snapshot its exact
	// position into the global store and pin it via dragBoundFunc (the drag session is never
	// stopped, so no re-click is needed). On exit, restore the snapshotted value and remove
	// the pin — the panels sit at their last frozen position until the user moves the mouse,
	// at which point they follow again naturally.
	useEffect(() => {
		const wasHidden = wasHiddenRef.current;
		wasHiddenRef.current = hideSatelliteOverlay;
		if (!isDraggingRef.current || activeDragFunctionality !== DRAG_FUNCTIONALITY) return;
		const group = groupRef.current;
		if (!group) return;

		if (hideSatelliteOverlay && !wasHidden) {
			const frozenPos = group.getAbsolutePosition();
			group.dragBoundFunc(() => frozenPos);
			dispatch(setMapOnlyFrozenPoint(frozenPos));
			return;
		}

		if (!hideSatelliteOverlay && wasHidden) {
			if (mapOnlyFrozenPoint) group.setAbsolutePosition(mapOnlyFrozenPoint);
			group.dragBoundFunc(undefined as any);
			dispatch(setMapOnlyFrozenPoint(null));
			group.getLayer()?.batchDraw();
		}
	}, [hideSatelliteOverlay, activeDragFunctionality, mapOnlyFrozenPoint, dispatch]);

	const handleDragStart = (e: KonvaEventObject<DragEvent>) => {
		const group = groupRef.current;
		const stage = konvaRef?.current;
		if (!group || !stage) return;

		isDraggingRef.current = true;
		dispatch(setActiveDragFunctionality(DRAG_FUNCTIONALITY));

		const allFacets = stage.find(`.${KonvaGroupNames.existingFacet}`) as Konva.Line[];
		const panels = stage.find((node: any) =>
			node instanceof Konva.Line &&
			node.name().includes('panel') &&
			!node.name().startsWith('selected-movable-panel')
		) as Konva.Line[];

		const facetGroupsNodes = group.find('.movable-panels-facet-group');
		const facetGroups = facetGroupsNodes.filter(
			(node): node is Konva.Group => node instanceof Konva.Group
		);

		allStablePanels.current = panels;
		allFacetsRef.current = allFacets;
		allMovablePanelsGroupRef.current = facetGroups;

		originalCenterByGroup.current.clear();
		facetGroups.forEach((g) => {
			originalCenterByGroup.current.set(g.id(), { cx: g.x(), cy: g.y() });
		});
	};

	// ------------- Drag Move -------------
	const handleDragMove = () => {
		const group = groupRef.current;
		const stage = konvaRef?.current;
		if (selectedMovablePanels.length === 0) {
			if (isInvalidPanelOperation) dispatch(setInvalidOperation({ isInvalidOperation: false }));
			return;
		}
		if (!group || !stage || !allFacetsRef.current) return;

		const intersectedFacet = findTargetFacetByCoverage(allFacetsRef.current, allMovablePanelsGroupRef.current, allRoofSegs, stage);
		const intersectedFacetId = intersectedFacet?.attrs.id;
		const targetFacetDetails = allRoofSegs.find(eachSeg => eachSeg.id === intersectedFacetId);
		intersectedFacetRef.current = intersectedFacet;
		let isCurrentFrameInvalid = false;

		let bestDelta = { x: 0, y: 0 };
		let bestDistance = Infinity;
		let foundAnySnap = false;

		const currentGroupPos = group.position();

		const siblingData = allStablePanels.current
			.filter(stable => !selectedMovablePanels.some(selected => selected.id === stable.id()))
			.map(stable => ({
				points: stable.points(),
				position: stable.position(),
				rotation: stable.rotation()
			}));

		if (intersectedFacet && intersectedFacetId && targetFacetDetails) {
			const targetAzimuth = targetFacetDetails.azimuthDegrees;
			const targetPitch = targetFacetDetails.pitchDegrees;

			allMovablePanelsGroupRef.current.forEach((facetPanelsGroup) => {
				if (!facetPanelsGroup) return;

				const facetData = panelsByFacet[facetPanelsGroup.attrs.facetId];
				if (!facetData) return;

				const originalPitch = facetData.originalPitch;

				// Calculate pitch scale factor
				const radOriginal = originalPitch * (Math.PI / 180);
				const radTarget = targetPitch * (Math.PI / 180);
				let scaleY = 1;
				if (Math.abs(Math.cos(radOriginal)) > 0.001) {
					scaleY = Math.cos(radTarget) / Math.cos(radOriginal);
				}

				const saved = originalCenterByGroup.current.get(facetPanelsGroup.id());
				if (!saved) return;
				// 		if (facetPanelsGroup.attrs.currentFacet !== intersectedFacetId) {
				// 	bakeGroupTransform(facetPanelsGroup);
				// 	facetPanelsGroup.setAttr('currentFacet', intersectedFacetId);
				// 	facetPanelsGroup.setAttr('currentAzimuth', targetAzimuth);
				// }
				facetPanelsGroup.setAttr('currentFacet', intersectedFacetId);
				facetPanelsGroup.setAttr('currentAzimuth', targetAzimuth);

				facetPanelsGroup.position({ x: saved.cx, y: saved.cy });
				facetPanelsGroup.rotation(targetAzimuth);
				facetPanelsGroup.scale({ x: 1, y: scaleY });

				const facetPts = normalizePointsToStage(intersectedFacet);

				facetPanelsGroup.children.forEach(eachChildPanel => {
					if (!(eachChildPanel instanceof Konva.Line)) return;

					const panelPts = normalizePointsToStage(eachChildPanel);

					// ==================== SNAPPING LOGIC ====================
					const localPoints = eachChildPanel.points();

					// Get the subgroup transform only (rotation relative to main group)
					const subGroupTransform = facetPanelsGroup.getTransform();

					// Transform the points with subgroup rotation, but keep relative to {x:0, y:0}
					const rotatedPointsRelativeToGroup: number[] = [];
					for (let i = 0; i < localPoints.length; i += 2) {
						const localPt = { x: localPoints[i], y: localPoints[i + 1] };
						// Apply only the subgroup transform (rotation around its pivot)
						const rotatedPt = subGroupTransform.point(localPt);
						rotatedPointsRelativeToGroup.push(rotatedPt.x, rotatedPt.y);
					}
					const snappedPos = getSnappedPositionForMultiplePanels(
						currentGroupPos,
						rotatedPointsRelativeToGroup,
						siblingData,
						3
					);

					if (snappedPos) {
						const deltaX = snappedPos.x - currentGroupPos.x;
						const deltaY = snappedPos.y - currentGroupPos.y;
						const distance = Math.hypot(deltaX, deltaY);

						if (distance > 0.5 && distance < bestDistance) {
							bestDistance = distance;
							bestDelta = { x: deltaX, y: deltaY };
							foundAnySnap = true;
						}
					}

					// CHECK BOUNDARY
					const inside = isCompletelyInsideFacet(panelPts, facetPts, 0.98);
					const movablePoly = generatePolygon(panelPts);

					// CHECK OVERLAP
					const overlaps = allStablePanels.current.some((stable) => {
						// if (stable.name().startsWith('selected-movable-panel')) return false;    
						if (stable.id() === eachChildPanel.id()) return false;
						const stableAbs = normalizePointsToStage(stable);
						const stablePoly = generatePolygon(stableAbs);
						return doesPolygonOverLap(
							movablePoly,
							stablePoly,
							2.3
						);
					});

					if (!inside || overlaps) {
						eachChildPanel.stroke('red');
						eachChildPanel.fill('rgba(255,0,0,0.25)');
						eachChildPanel.setAttr('isValid', false);
						isCurrentFrameInvalid = true;
					} else {
						eachChildPanel.stroke('gray');
						eachChildPanel.fill('blue');
						eachChildPanel.setAttr('isValid', true);
						eachChildPanel.setAttr('currentFacet', intersectedFacetId);
					}
				});
			});

			if (foundAnySnap) {
				group.position({
					x: currentGroupPos.x + bestDelta.x,
					y: currentGroupPos.y + bestDelta.y
				});
			}
		} else {
			isCurrentFrameInvalid = true;
			allMovablePanelsGroupRef.current.forEach((facetPanelsGroup) => {
				// facetPanelsGroup.scale({ x: 1, y: 1 });
				facetPanelsGroup.children.forEach(eachChildPanel => {
					if (eachChildPanel instanceof Konva.Line) {
						eachChildPanel.stroke('red');
						eachChildPanel.fill('rgba(255,0,0,0.25)');
						eachChildPanel.setAttr('isValid', false);
					}
				});
			});
		}

		if (isCurrentFrameInvalid !== isInvalidPanelOperation) {
			dispatch(setInvalidOperation({ isInvalidOperation: isCurrentFrameInvalid }));
		}
		transformerRef.current?.forceUpdate();

		group.getStage()?.batchDraw();
	};

	// ------------- Drag End -------------
	const handleDragEnd = () => {
		const group = groupRef.current;
		const stage = konvaRef?.current;
		const pointer = stage?.getPointerPosition();

		isDraggingRef.current = false;
		dispatch(setActiveDragFunctionality(null));
		dispatch(setMapOnlyFrozenPoint(null));
		group?.dragBoundFunc(undefined as any);

		if (!pointer || !group || !stage || !allFacetsRef.current) return;

		const facet = intersectedFacetRef.current;
		const validPanels: PanelInfo[] = [];
		const mainGroupTransform = group.getTransform();
		let isInvalidOperation = false;

		allMovablePanelsGroupRef.current.forEach(subGroup => {
			const subGroupTransform = subGroup.getTransform();

			subGroup.children.forEach(child => {
				if (!(child instanceof Konva.Line)) return;
				const eachPanel = child;

				const points = ensureClosedPolygon(eachPanel.points());

				const exteriorCoords: number[][] = [];
				for (let i = 0; i < points.length; i += 2) {
					const localPt = { x: points[i], y: points[i + 1] };
					const pointInMain = subGroupTransform.point(localPt);
					const canvasPt = mainGroupTransform.point(pointInMain);

					exteriorCoords.push([
						canvasPt.x / scale.x,
						canvasPt.y / scale.y
					]);
				}

				const panelId = eachPanel.attrs.panelId;
				const originSegmentId = eachPanel.attrs.originSegment;
				const orientation = eachPanel.attrs.orientation;
				const targetSegmentId = facet ? facet.attrs.id : eachPanel.attrs.currentFacet;

				const isValid = eachPanel.getAttr('isValid');
				if (!isValid) {
					isInvalidOperation = true;
				}

				validPanels.push({
					panelId,
					originSegmentId,
					roofSegmentId: targetSegmentId,
					orientation,
					exteriorCoords,
					isValid: isValid
				});
			});
		});

		if (validPanels.length > 0) {
			dispatch(
				updateMovedPanelsPosition({
					movedPanels: validPanels,
					isInvalidOperation: isInvalidOperation
				})
			);
		}
		group.position({ x: 0, y: 0 });

		allFacetsRef.current = [];
		allStablePanels.current = [];
		allMovablePanelsGroupRef.current = [];
		intersectedFacetRef.current = null;
		stage.batchDraw();
	};

	const handleClick = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
		const group = groupRef.current;
		const targetPanel = e.target;
		const isValidPanel = e.target.attrs.isValid;
		if (!(targetPanel instanceof Konva.Line)) return;
		if (!isValidPanel) return;
		const subGroupTransform = targetPanel.getParent()?.getTransform();
		const mainGroupTransform = group?.getTransform();
		if (!subGroupTransform || !mainGroupTransform) return;

		const points = ensureClosedPolygon(targetPanel.points());

		const exteriorCoords: number[][] = [];
		for (let i = 0; i < points.length; i += 2) {
			const localPt = { x: points[i], y: points[i + 1] };
			const pointInMain = subGroupTransform.point(localPt);
			const canvasPt = mainGroupTransform.point(pointInMain);
			exteriorCoords.push([
				canvasPt.x / scale.x,
				canvasPt.y / scale.y
			]);
		}
		dispatch(
			setToggleAddSelectedMovablePanel({
				panelId: targetPanel.attrs.panelId
			})
		);
	};

	return (
		<>
			<Group
				ref={groupRef}
				id='selected-panels-group'
				draggable={true}
				onDragStart={handleDragStart}
				onDragMove={handleDragMove}
				onDragEnd={handleDragEnd}
				dragDistance={7}
			>
				{Object.entries(panelsByFacet).map(([facetId, groupData]) => (
					<Group
						key={facetId}
						id={`movable-panels-group-${facetId}`}
						name='movable-panels-facet-group'
						currentAzimuth={groupData.currentAzimuth}
						facetId={facetId}
						currentFacet={facetId}
						x={groupData.centerX}
						y={groupData.centerY}
						rotation={groupData.currentAzimuth}
					>
						{groupData.panels.map(panel => {
							const isInvalid = (panel as any).isValid === false;

							return (
								<Line
									key={panel.id}
									id={panel.id}
									closed
									stroke={isInvalid ? 'red' : 'gray'}
									strokeWidth={1.4}
									strokeScaleEnabled={false}
									points={panel.normalizedPoints}
									fill={isInvalid ? 'rgba(255,0,0,0.25)' : 'blue'}
									name={`selected-movable-panel-${panel.segmentId}-${panel.id}`}
									onClick={handleClick}
									onTap={handleClick}
									panelId={panel.id}
									originSegment={panel.originSegmentId}
									orientation={panel.orientation}
									currentFacet={panel.segmentId}
									isValid={!isInvalid}
								/>
							);
						})}
					</Group>
				))}

			</Group>
			{/* <Transformer ref={transformerRef} /> */}
		</>
	);
}

export default memo(SelectedMovablePanelsGroup);